import { Card, Image, Text, Stack, Box } from "@mantine/core";
import { V2Source, V2Delta } from "../../api";

interface SourceCardProps {
    source: V2Source;
    number: number;
    response: V2Delta | undefined;
    onClick: (source: V2Source, anchor: string) => void;
}

export const SourceCard = ({ source, number, response, onClick }: SourceCardProps) => {
    const getThumbnailUrl = (source: V2Source): string => {
        if (source.thumbnailUrl) {
            return source.thumbnailUrl;
        }

        return "/static/document.webp";
    };

    const getFirstCitationAnchor = (): string => {
        if (!response?.response) {
            return "";
        }

        // Find the first markdown link for this source's citation number: [N...](url) or [N...](url#anchor)
        // Format can be [1](url) or [1 page 2](url#page=2) - number may be followed by arbitrary text
        // Negative lookahead (?![0-9]) ensures we don't match [12] when looking for [1]
        const citationPattern = new RegExp(`\\[${number}(?![0-9])[^\\]]*\\]\\(([^)]+)\\)`, "g");
        const match = citationPattern.exec(response.response);

        if (match && match[1]) {
            const href = match[1];
            if (href.includes("#")) {
                return href.split("#")[1] || "";
            }
        }

        return "";
    };

    const handleClick = () => {
        const anchor = getFirstCitationAnchor();
        onClick(source, anchor);
    };

    return (
        <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            w="100%"
            h="100%" // makes the card stretch to match grid height
            style={{
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Card.Section style={{ cursor: "pointer" }} onClick={handleClick}>
                <Image src={getThumbnailUrl(source)} height={160} alt={source.title} />
            </Card.Section>
            {/* This Box must grow to fill all available vertical space */}
            <Box
                style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    justifyContent: "space-between" // distributes content and pushes bottom row
                }}
            >
                <Stack style={{ cursor: "pointer" }} mt="sm" gap={4} onClick={handleClick}>
                    <Text fw={500}>
                        [{number}] {source.title}
                    </Text>
                    <Text c="gray.6" size="sm">
                        {(() => {
                            const date = source.rankingTimestamp ?? source.lastModifiedOn;
                            return date != null ? new Date(date).toLocaleDateString() : "";
                        })()}
                    </Text>
                    <Text c="gray.6" size="sm">
                        {source.resourceType || source.dataSource}
                    </Text>
                </Stack>
            </Box>
        </Card>
    );
};
